import React, { useState, useMemo, useEffect } from 'react';
import {
  Truck, Package, CheckCircle, Clock, AlertCircle,
  Search, X, ChevronRight, User, Weight
} from 'lucide-react';
import { DELIVERY_STATUS, DELIVERY_STATUS_LABELS, DELIVERY_STATUS_COLORS } from '../constants/status';

// 状态筛选选项（分组简化）
const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: 'pending', label: '待处理', statuses: [DELIVERY_STATUS.WAITING_DELIVERY, DELIVERY_STATUS.ARRIVED, DELIVERY_STATUS.WAITING_APPROVAL, DELIVERY_STATUS.PENDING_SETTLEMENT] },
  { value: 'stored', label: '已入库', statuses: [DELIVERY_STATUS.STORED] },
  { value: 'settled', label: '已结算', statuses: [DELIVERY_STATUS.SETTLED] },
  { value: 'invoiced', label: '已开票', statuses: [DELIVERY_STATUS.INVOICED] },
  { value: 'paid', label: '已付款', statuses: [DELIVERY_STATUS.PAID] },
];


// 状态徽章组件
const StatusBadge = ({ status }) => {
  const config = DELIVERY_STATUS_COLORS[status];

  if (!config) return null;

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${config.bg} ${config.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {DELIVERY_STATUS_LABELS[status]}
    </span>
  );
};

// 统计卡片组件
const StatCard = ({ title, value, icon: Icon, iconColor, bgColor }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-slate-500">{title}</p>
        <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
        <Icon size={24} className={iconColor} />
      </div>
    </div>
  </div>
);

// 主组件
export default function DeliveryView({ deliveries, orders, onDeliveryClick, onOrderClick, initialFilter, setInitialFilter }) {
  // 筛选状态
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterBase, setFilterBase] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');

  // 处理从首页传递的初始筛选条件
  useEffect(() => {
    if (initialFilter) {
      if (initialFilter.status) {
        setFilterStatus(initialFilter.status);
      }
      setInitialFilter?.(null);
    }
  }, [initialFilter, setInitialFilter]);

  // 计算统计数据
  const stats = useMemo(() => {
    const pendingStatuses = [
      DELIVERY_STATUS.WAITING_DELIVERY,
      DELIVERY_STATUS.ARRIVED,
      DELIVERY_STATUS.WAITING_APPROVAL,
      DELIVERY_STATUS.PENDING_SETTLEMENT
    ];
    const completedStatuses = [
      DELIVERY_STATUS.SETTLED,
      DELIVERY_STATUS.INVOICED,
      DELIVERY_STATUS.PAID
    ];

    const pendingCount = deliveries.filter(d => pendingStatuses.includes(d.status)).length;
    const storedCount = deliveries.filter(d => d.status === DELIVERY_STATUS.STORED).length;
    const completedCount = deliveries.filter(d => completedStatuses.includes(d.status)).length;

    // 计算送货总吨位（已入库的实重之和）
    const totalWeight = deliveries
      .filter(d => d.storage?.actualWeight)
      .reduce((sum, d) => sum + (d.storage.actualWeight || 0), 0);

    // 等待审批数量
    const waitingApprovalCount = deliveries.filter(d => d.status === DELIVERY_STATUS.WAITING_APPROVAL).length;

    return {
      total: deliveries.length,
      pending: pendingCount,
      stored: storedCount,
      completed: completedCount,
      totalWeight: totalWeight.toFixed(1),
      waitingApproval: waitingApprovalCount,
    };
  }, [deliveries]);

  // 提取筛选选项
  const { suppliers, bases } = useMemo(() => {
    const supplierSet = new Set();
    const baseSet = new Set();

    deliveries.forEach(d => {
      if (d.supplierName) supplierSet.add(d.supplierName);
      if (d.baseName) baseSet.add(d.baseName);
    });

    return {
      suppliers: Array.from(supplierSet).sort(),
      bases: Array.from(baseSet).sort(),
    };
  }, [deliveries]);

  // 是否有活跃筛选
  const hasActiveFilters = searchQuery || filterStatus || filterSupplier || filterBase || filterDateStart || filterDateEnd;

  // 清除筛选
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterSupplier('');
    setFilterBase('');
    setFilterDateStart('');
    setFilterDateEnd('');
  };

  // 筛选送货单
  const filteredDeliveries = useMemo(() => {
    let result = [...deliveries];

    // 搜索筛选
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        d.deliveryNo.toLowerCase().includes(query) ||
        d.supplierName.toLowerCase().includes(query) ||
        d.reservation?.vehicleNo?.toLowerCase().includes(query) ||
        d.orderNo.toLowerCase().includes(query)
      );
    }

    // 状态筛选
    if (filterStatus) {
      const option = STATUS_OPTIONS.find(o => o.value === filterStatus);
      if (option && option.statuses) {
        result = result.filter(d => option.statuses.includes(d.status));
      }
    }

    // 供应商筛选
    if (filterSupplier) {
      result = result.filter(d => d.supplierName === filterSupplier);
    }

    // 基地筛选
    if (filterBase) {
      result = result.filter(d => d.baseName === filterBase);
    }

    // 日期范围筛选
    if (filterDateStart) {
      result = result.filter(d => {
        const date = d.reservation?.time || d.createdAt;
        return date && date >= filterDateStart;
      });
    }
    if (filterDateEnd) {
      result = result.filter(d => {
        const date = d.reservation?.time || d.createdAt;
        return date && date <= filterDateEnd + ' 23:59:59';
      });
    }

    // 按时间倒序
    result.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

    return result;
  }, [deliveries, searchQuery, filterStatus, filterSupplier, filterBase, filterDateStart, filterDateEnd]);

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">送货单管理</h1>
          <p className="text-slate-500 text-sm mt-1">管理所有送货单据</p>
        </div>
        {/* 等待审批提醒 */}
        {stats.waitingApproval > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle size={18} className="text-amber-500" />
            <span className="text-sm text-amber-700 font-medium">
              {stats.waitingApproval} 辆车等待订单审批
            </span>
          </div>
        )}
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          title="全部送货单"
          value={stats.total}
          icon={Truck}
          iconColor="text-slate-600"
          bgColor="bg-slate-50"
        />
        <StatCard
          title="待处理"
          value={stats.pending}
          icon={Clock}
          iconColor="text-amber-600"
          bgColor="bg-amber-50"
        />
        <StatCard
          title="已入库"
          value={stats.stored}
          icon={Package}
          iconColor="text-purple-600"
          bgColor="bg-purple-50"
        />
        <StatCard
          title="已完成"
          value={stats.completed}
          icon={CheckCircle}
          iconColor="text-green-600"
          bgColor="bg-green-50"
        />
        <StatCard
          title="送货总吨位"
          value={`${stats.totalWeight} 吨`}
          icon={Weight}
          iconColor="text-blue-600"
          bgColor="bg-blue-50"
        />
      </div>

      {/* 筛选区域 */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap gap-3">
            {/* 搜索框 */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="搜索单号、供应商、车牌号、订单号..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 状态筛选 */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            {/* 供应商筛选 */}
            <select
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">全部供应商</option>
              {suppliers.map(supplier => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>

            {/* 基地筛选 */}
            <select
              value={filterBase}
              onChange={(e) => setFilterBase(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">全部基地</option>
              {bases.map(base => (
                <option key={base} value={base}>{base}</option>
              ))}
            </select>

            {/* 日期范围 */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={filterDateStart}
                onChange={(e) => setFilterDateStart(e.target.value)}
                className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
              <span className="text-slate-400">~</span>
              <input
                type="date"
                value={filterDateEnd}
                onChange={(e) => setFilterDateEnd(e.target.value)}
                className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 清除筛选 */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                <X size={16} />
                清除筛选
              </button>
            )}
          </div>
        </div>

        {/* 表格区域 */}
        {filteredDeliveries.length === 0 ? (
          <div className="p-12 text-center">
            <Truck size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">暂无送货单</p>
            {hasActiveFilters && (
              <p className="text-sm text-slate-400 mt-1">尝试调整筛选条件</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">送货单号</th>
                  <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">订单号</th>
                  <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">供应商</th>
                  <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">基地</th>
                  <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">车牌号</th>
                  <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">预约时间</th>
                  <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">实重(吨)</th>
                  <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">含税金额</th>
                  <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">状态</th>
                  <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDeliveries.map(delivery => {
                  const isWaitingApproval = delivery.status === DELIVERY_STATUS.WAITING_APPROVAL;
                  const hasStorage = !!delivery.storage;

                  return (
                    <tr
                      key={delivery.id}
                      className={`transition-colors ${
                        isWaitingApproval
                          ? 'bg-amber-50 hover:bg-amber-100'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="p-3">
                        <span className="font-mono text-xs text-slate-600">
                          {delivery.deliveryNo}
                        </span>
                        {isWaitingApproval && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-200 text-amber-700 rounded font-medium">
                            需关注
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOrderClick) {
                              const order = orders.find(o => o.id === delivery.orderId);
                              if (order) onOrderClick(order);
                            }
                          }}
                          className="font-mono text-xs text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          {delivery.orderNo}
                        </button>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center">
                            <User size={14} className="text-slate-500" />
                          </div>
                          <span className="text-sm text-slate-800">{delivery.supplierName}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-slate-600">{delivery.baseName}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm font-mono text-slate-700">
                          {delivery.reservation?.vehicleNo || '-'}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-slate-600">
                          {delivery.reservation?.time || '-'}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        {hasStorage ? (
                          <span className="text-sm font-medium text-slate-800">
                            {delivery.storage.actualWeight}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {hasStorage && delivery.storage.amountWithTax ? (
                          <span className="text-sm font-bold text-orange-600">
                            ¥{delivery.storage.amountWithTax.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <StatusBadge status={delivery.status} />
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() => onDeliveryClick(delivery)}
                          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 mx-auto"
                        >
                          详情
                          <ChevronRight size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 底部统计 */}
        {filteredDeliveries.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <p className="text-sm text-slate-500">
              共 <span className="font-medium text-slate-700">{filteredDeliveries.length}</span> 条记录
              {hasActiveFilters && (
                <span className="ml-2">
                  （筛选自 <span className="font-medium text-slate-700">{deliveries.length}</span> 条）
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
